import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { formatJPY, formatDate, todayISO } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Loader2, Receipt as ReceiptIcon, Upload, Paperclip, FileSpreadsheet, X, Sparkles, Camera } from "lucide-react";
import { toast } from "sonner";
import CropModal from "@/components/CropModal";
import { isCroppableImage } from "@/lib/imageProcess";

const CATEGORIES = [
  "交通費", "通信費", "消耗品費", "接待交際費", "会議費",
  "新聞図書費", "広告宣伝費", "支払手数料", "外注費", "地代家賃",
  "水道光熱費", "雑費", "その他",
];

const PAYMENT_METHODS = [
  { v: "cash", label: "現金" },
  { v: "credit_card", label: "クレジットカード" },
  { v: "bank_transfer", label: "銀行振込" },
  { v: "other", label: "その他" },
];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [receiptCropSrc, setReceiptCropSrc] = useState(null);
  const [cropMode, setCropMode] = useState("attach");
  const fileInputRef = useRef(null);
  const aiInputRef = useRef(null);
  const aiCameraRef = useRef(null);
  const receiptCameraRef = useRef(null);
  const [form, setForm] = useState({
    date: todayISO(),
    category: "交通費",
    amount: 0,
    description: "",
    vendor: "",
    payment_method: "cash",
    tax_category: "課税10%",
    tax_amount: 0,
    receipt_file_id: "",
    receipt_filename: "",
  });

  // CSV import state
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvCommitting, setCsvCommitting] = useState(false);
  const csvInputRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses");
      setExpenses(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "読み込みエラー");
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    if (!form.description || !form.amount) {
      toast.error("摘要と金額を入力してください");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/expenses", {
        date: form.date,
        category: form.category,
        amount: Number(form.amount),
        description: form.description,
        vendor: form.vendor,
        payment_method: form.payment_method,
        tax_category: form.tax_category || "課税10%",
        receipt_file_id: form.receipt_file_id || "",
      });
      toast.success("経費を記録しました");
      setOpen(false);
      setForm({
        date: todayISO(), category: "交通費", amount: 0, description: "",
        vendor: "", payment_method: "cash", tax_category: "課税10%", tax_amount: 0,
        receipt_file_id: "", receipt_filename: "",
      });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSubmitting(false);
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("ファイルサイズが大きすぎます (最大10MB)");
      return;
    }
    if (isCroppableImage(file)) {
      setCropMode("attach");
      const reader = new FileReader();
      reader.onload = () => setReceiptCropSrc(reader.result);
      reader.readAsDataURL(file);
      return;
    }
    uploadReceiptBlob(file, file.name || "receipt.pdf");
  };

  const onAiSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("ファイルサイズが大きすぎます (最大10MB)");
      return;
    }
    if (isCroppableImage(file)) {
      setCropMode("ai");
      const reader = new FileReader();
      reader.onload = () => setReceiptCropSrc(reader.result);
      reader.readAsDataURL(file);
      return;
    }
    runAi(file, file.name || "receipt.pdf");
  };

  const mapOcrToForm = (r) => {
    const patch = {};
    if (r.date) patch.date = String(r.date).slice(0, 10);
    if (r.vendor) patch.vendor = r.vendor;
    if (r.description) patch.description = r.description;
    if (r.amount != null && !isNaN(Number(r.amount))) {
      const amt = Number(r.amount);
      patch.amount = amt;
      const rate = Number(r.tax_rate);
      if (!isNaN(rate) && rate > 0) {
        patch.tax_amount = Math.round((amt * rate) / (100 + rate));
      }
    }
    // 税区分マッピング (AIの tax_rate / tax_category から)
    const rate = Number(r.tax_rate);
    const cat = String(r.tax_category || "");
    if (cat.includes("軽減") || rate === 8) patch.tax_category = "軽減8%";
    else if (cat.includes("非課税")) patch.tax_category = "非課税";
    else if (cat.includes("対象外") || cat.includes("不課税")) patch.tax_category = "不課税";
    else if (rate === 10 || cat.includes("課税")) patch.tax_category = "課税10%";
    if (r.suggested_account) {
      const name = String(r.suggested_account).includes(":")
        ? String(r.suggested_account).split(":").pop().trim()
        : String(r.suggested_account).trim();
      const match = CATEGORIES.find((c) => name.includes(c) || c.includes(name));
      if (match) patch.category = match;
    }
    return patch;
  };

  const runAi = async (fileOrBlob, filename) => {
    setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileOrBlob, filename);
      const ocrPromise = api.post("/ai/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fd2 = new FormData();
      fd2.append("file", fileOrBlob, filename);
      const uploadPromise = api.post("/uploads/receipt", fd2, { headers: { "Content-Type": "multipart/form-data" } });
      const [ocrRes, upRes] = await Promise.all([ocrPromise, uploadPromise]);
      setForm((f) => ({
        ...f,
        ...mapOcrToForm(ocrRes.data),
        receipt_file_id: upRes.data.id,
        receipt_filename: upRes.data.original_filename,
      }));
      toast.success("AIが読み取り、フォームに自動入力しました");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "自動入力に失敗しました");
    } finally {
      setAiBusy(false);
      if (aiInputRef.current) aiInputRef.current.value = "";
    }
  };

  const uploadReceiptBlob = async (fileOrBlob, filename) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileOrBlob, filename);
      const res = await api.post("/uploads/receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        receipt_file_id: res.data.id,
        receipt_filename: res.data.original_filename,
      }));
      toast.success("領収書をアップロードしました");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "アップロード失敗");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleReceiptCropApply = async (blob) => {
    setReceiptCropSrc(null);
    if (cropMode === "ai") {
      await runAi(blob, "receipt.jpg");
    } else {
      await uploadReceiptBlob(blob, "receipt.jpg");
    }
  };

  const cancelReceiptCrop = () => {
    setReceiptCropSrc(null);
    [fileInputRef, aiInputRef, aiCameraRef, receiptCameraRef].forEach((r) => { if (r.current) r.current.value = ""; });
  };

  const removeReceipt = () =>
    setForm((f) => ({ ...f, receipt_file_id: "", receipt_filename: "" }));

  const handleCsvSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    setCsvPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/expenses/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvPreview(res.data.items.map((it, i) => ({ ...it, _selected: true, _idx: i })));
      if (!res.data.items.length) toast.info("インポート可能な出金データがありませんでした");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "CSV解析に失敗");
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  const handleCsvCommit = async () => {
    if (!csvPreview) return;
    const items = csvPreview.filter((it) => it._selected).map(({ _selected, _idx, ...rest }) => rest);
    if (!items.length) {
      toast.error("インポートする項目を選択してください");
      return;
    }
    setCsvCommitting(true);
    try {
      const res = await api.post("/expenses/import/commit", { items });
      toast.success(`${res.data.inserted}件の経費を登録しました`);
      setCsvOpen(false);
      setCsvPreview(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCsvCommitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("この経費を削除しますか？")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("削除しました");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const RATE_BY_CAT = { "課税10%": 10, "軽減8%": 8, "非課税": 0, "不課税": 0 };
  const computedTax = (() => {
    const rate = RATE_BY_CAT[form.tax_category] ?? 0;
    const amt = Number(form.amount) || 0;
    return rate > 0 ? Math.round((amt * rate) / (100 + rate)) : 0;
  })();

  return (
    <div className="space-y-6" data-testid="expenses-root">
      <CropModal src={receiptCropSrc} onCancel={cancelReceiptCrop} onApply={handleReceiptCropApply} busy={uploading} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">経費</div>
          <h1 className="mt-1 font-display font-bold text-3xl tracking-tight text-stone-900">経費管理</h1>
          <p className="mt-1 text-sm text-stone-500">合計 {expenses.length} 件 ・ {formatJPY(total)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Dialog open={csvOpen} onOpenChange={(v) => { setCsvOpen(v); if (!v) setCsvPreview(null); }}>
            <DialogTrigger asChild>
              <button
                data-testid="csv-import-button"
                className="inline-flex items-center gap-2 rounded-md border border-stone-300 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                <FileSpreadsheet className="h-4 w-4" /> CSV取込
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">銀行明細CSVの取込</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-xs text-stone-500">
                  対応形式: 汎用CSV (日付・摘要・金額) または 三菱UFJ / みずほ / 三井住友 等の主要邦銀フォーマット (Shift_JIS / UTF-8)。
                  出金 (引落) のみが経費として取り込まれます。
                </p>
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleCsvSelect}
                  data-testid="csv-file-input"
                />
                {!csvPreview ? (
                  <button
                    onClick={() => csvInputRef.current?.click()}
                    disabled={csvUploading}
                    data-testid="csv-select-button"
                    className="w-full border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
                  >
                    {csvUploading ? (
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-emerald-900" />
                    ) : (
                      <>
                        <Upload className="h-8 w-8 mx-auto text-stone-400 mb-2" strokeWidth={1.25} />
                        <div className="text-sm font-medium text-stone-900">CSVファイルを選択</div>
                        <div className="text-xs text-stone-500 mt-1">クリックしてアップロード</div>
                      </>
                    )}
                  </button>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-stone-900">{csvPreview.length} 件の出金データを検出</span>
                      <button
                        onClick={() => setCsvPreview(null)}
                        className="text-xs text-stone-500 hover:text-stone-900"
                        data-testid="csv-reset-button"
                      >
                        ファイルを変更
                      </button>
                    </div>
                    <div className="border border-stone-200 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-stone-50/70 sticky top-0">
                          <tr className="text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                            <th className="px-3 py-2 text-left">取込</th>
                            <th className="px-3 py-2 text-left">日付</th>
                            <th className="px-3 py-2 text-left">摘要</th>
                            <th className="px-3 py-2 text-left">カテゴリ</th>
                            <th className="px-3 py-2 text-right">金額</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreview.map((it, i) => (
                            <tr key={i} className="border-t border-stone-100" data-testid={`csv-preview-row-${i}`}>
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={it._selected}
                                  data-testid={`csv-row-checkbox-${i}`}
                                  onChange={(e) => {
                                    const next = [...csvPreview];
                                    next[i]._selected = e.target.checked;
                                    setCsvPreview(next);
                                  }}
                                  className="h-4 w-4 accent-emerald-900"
                                />
                              </td>
                              <td className="px-3 py-2 text-stone-500">{it.date}</td>
                              <td className="px-3 py-2 text-stone-900">{it.description}</td>
                              <td className="px-3 py-2">
                                <select
                                  value={it.category}
                                  onChange={(e) => {
                                    const next = [...csvPreview];
                                    next[i].category = e.target.value;
                                    setCsvPreview(next);
                                  }}
                                  className="h-8 rounded-md border border-stone-300 bg-white px-1.5 text-xs"
                                  data-testid={`csv-category-${i}`}
                                >
                                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2 text-right font-numbers font-medium text-stone-900">{formatJPY(it.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <button onClick={() => setCsvOpen(false)} className="rounded-md border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">キャンセル</button>
                {csvPreview && (
                  <button
                    onClick={handleCsvCommit}
                    disabled={csvCommitting}
                    data-testid="csv-commit-button"
                    className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60"
                  >
                    {csvCommitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    選択した項目を取込
                  </button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="expense-create-button"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-950"
            >
              <Plus className="h-4 w-4" /> 経費を登録
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-display">経費の登録</DialogTitle>
            </DialogHeader>
            <input
              ref={aiInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={onAiSelect}
              data-testid="expense-ai-input"
            />
            <input
              ref={aiCameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onAiSelect}
              data-testid="expense-ai-camera"
            />
            <div className="mb-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => aiCameraRef.current?.click()}
                disabled={aiBusy}
                data-testid="expense-ai-camera-btn"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-emerald-800 to-emerald-950 px-4 py-3 text-sm font-medium text-white hover:from-emerald-900 hover:to-black disabled:opacity-70"
              >
                {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                {aiBusy ? "AI読み取り中…" : "撮影して自動入力"}
              </button>
              <button
                type="button"
                onClick={() => aiInputRef.current?.click()}
                disabled={aiBusy}
                data-testid="expense-ai-autofill"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-70"
              >
                <Sparkles className="h-4 w-4" />
                写真を選択(AI)
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">日付</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="expense-date" />
              </div>
              <div>
                <Label className="text-sm">カテゴリ</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`expense-category-${c}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-sm">摘要</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="例: 顧客打ち合わせのタクシー代" className="mt-1.5" data-testid="expense-description" />
              </div>
              <div>
                <Label className="text-sm">金額 (税込)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="expense-amount" />
              </div>
              <div>
                <Label className="text-sm">税区分</Label>
                <Select value={form.tax_category} onValueChange={(v) => setForm({ ...form, tax_category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="expense-tax-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="課税10%">課税 10%</SelectItem>
                    <SelectItem value="軽減8%">軽減税率 8%</SelectItem>
                    <SelectItem value="非課税">非課税</SelectItem>
                    <SelectItem value="不課税">不課税 (対象外)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-sm">うち消費税 (自動計算)</Label>
                <Input type="number" value={computedTax} readOnly disabled className="mt-1.5 bg-stone-50" data-testid="expense-tax" />
              </div>
              <div>
                <Label className="text-sm">支払先</Label>
                <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="mt-1.5" data-testid="expense-vendor" placeholder="任意" />
              </div>
              <div>
                <Label className="text-sm">支払方法</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="expense-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((p) => (
                      <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-sm">領収書 (画像 / PDF)</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={handleReceiptUpload}
                  data-testid="expense-receipt-input"
                />
                <input
                  ref={receiptCameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleReceiptUpload}
                  data-testid="expense-receipt-camera"
                />
                <div className="mt-1.5">
                  {form.receipt_file_id ? (
                    <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                      <span className="flex items-center gap-2 text-emerald-900">
                        <Paperclip className="h-4 w-4" />
                        <span className="truncate">{form.receipt_filename}</span>
                      </span>
                      <button onClick={removeReceipt} className="text-stone-500 hover:text-orange-700" data-testid="expense-receipt-remove">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => receiptCameraRef.current?.click()}
                        disabled={uploading}
                        data-testid="expense-receipt-camera-btn"
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-dashed border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-60"
                      >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                        撮影
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="expense-receipt-upload"
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-dashed border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-60"
                      >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        ファイル選択
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setOpen(false)} className="rounded-md border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">キャンセル</button>
              <button
                onClick={handleCreate}
                disabled={submitting}
                data-testid="expense-submit-button"
                className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />} 登録する
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-emerald-900" /></div>
        ) : expenses.length === 0 ? (
          <div className="p-10 text-center text-stone-500">
            <ReceiptIcon className="h-10 w-10 mx-auto mb-3 text-stone-300" strokeWidth={1.25} />
            経費の記録がありません
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-stone-500 uppercase tracking-wider bg-stone-50/50 border-b border-stone-200">
                  <th className="px-6 py-3 text-left font-semibold">日付</th>
                  <th className="px-6 py-3 text-left font-semibold">カテゴリ</th>
                  <th className="px-6 py-3 text-left font-semibold">摘要</th>
                  <th className="px-6 py-3 text-left font-semibold">支払先</th>
                  <th className="px-6 py-3 text-right font-semibold">金額</th>
                  <th className="px-6 py-3 text-right font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((ex) => (
                  <tr key={ex.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`expense-row-${ex.id}`}>
                    <td className="px-6 py-3 text-stone-500">{formatDate(ex.date)}</td>
                    <td className="px-6 py-3">
                      <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2.5 py-0.5 text-xs font-medium text-stone-700">
                        {ex.category}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-stone-900">{ex.description}</td>
                    <td className="px-6 py-3 text-stone-500">{ex.vendor || "—"}</td>
                    <td className="px-6 py-3 text-right font-numbers font-medium text-stone-900">{formatJPY(ex.amount)}</td>
                    <td className="px-6 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {ex.receipt_file_id && (
                          <a
                            href={`${API_BASE}/files/${ex.receipt_file_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`expense-receipt-link-${ex.id}`}
                            title="領収書を表示"
                            className="h-8 w-8 rounded-md hover:bg-stone-100 inline-flex items-center justify-center text-emerald-800"
                          >
                            <Paperclip className="h-4 w-4" />
                          </a>
                        )}
                        <button
                          onClick={() => handleDelete(ex.id)}
                          data-testid={`expense-delete-${ex.id}`}
                          className="h-8 w-8 rounded-md hover:bg-orange-50 inline-flex items-center justify-center text-stone-400 hover:text-orange-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
